import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageShell, DataTable } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData, useDebouncedSearch } from '../../hooks/useLiveData';
import { useHospitalDirectory, formatDoctorOption } from '../../hooks/useHospitalDirectory';
import { Q } from '../../config/queueStatus';
import VaccinationChart from '../../components/emr/VaccinationChart';
import { mergeEmrSettings } from '../../config/emrSettings';
import { canMergePatients } from '../../permissions';
import { buildIpdAdmitLink } from '../../utils/opIpLink';

const TITLES = ['', 'Mr', 'Mrs', 'Ms', 'Master', 'Baby', 'Dr', 'Smt'];
const BLOOD_GROUPS = ['', 'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const MARITAL = ['', 'Single', 'Married', 'Widowed', 'Divorced'];

const emptyForm = {
  title: '', name: '', phone: '', gender: 'M', dob: '', age: '', bloodGroup: '',
  email: '', address: '', city: '', pincode: '', occupation: '', maritalStatus: '',
  abhaId: '', emergencyName: '', emergencyPhone: '',
  allergies: '', chronicConditions: '', currentMeds: '',
};

function ageFromDob(dob) {
  if (!dob) return '';
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const yrs = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  return yrs >= 0 ? String(yrs) : '';
}

function initials(name = '') {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
}

function toForm(row) {
  return {
    title: row.title || '', name: row.name || '', phone: row.phone || '', gender: row.gender || 'M',
    dob: row.dob || '', age: row.age || '', bloodGroup: row.bloodGroup || '', email: row.email || '',
    address: row.address || '', city: row.city || '', pincode: row.pincode || '',
    occupation: row.occupation || '', maritalStatus: row.maritalStatus || '', abhaId: row.abhaId || '',
    emergencyName: row.emergencyName || '', emergencyPhone: row.emergencyPhone || '',
    allergies: row.allergies || '',
    chronicConditions: Array.isArray(row.chronicConditions) ? row.chronicConditions.join(', ') : (row.chronicConditions || ''),
    currentMeds: row.currentMeds || '',
  };
}

function serialize(f) {
  return {
    ...f,
    age: Number(f.age) || f.age,
    chronicConditions: f.chronicConditions ? f.chronicConditions.split(',').map((s) => s.trim()).filter(Boolean) : [],
  };
}

function PatientFormFields({ form, set }) {
  return (
    <>
      <div className="form-section-title">Identity</div>
      <div className="field"><label>Title</label><select className="input" value={form.title} onChange={(e) => set('title', e.target.value)}>{TITLES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}</select></div>
      <div className="field"><label>Full name *</label><input className="input" required value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
      <div className="field"><label>Gender</label><select className="input" value={form.gender} onChange={(e) => set('gender', e.target.value)}><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option></select></div>
      <div className="field"><label>Date of birth</label><input className="input" type="date" value={form.dob} onChange={(e) => { const dob = e.target.value; set('dob', dob); const a = ageFromDob(dob); if (a) set('age', a); }} /></div>
      <div className="field"><label>Age</label><input className="input" value={form.age} onChange={(e) => set('age', e.target.value)} /></div>
      <div className="field"><label>Blood group</label><select className="input" value={form.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)}>{BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b || '—'}</option>)}</select></div>

      <div className="form-section-title">Contact</div>
      <div className="field"><label>Phone *</label><input className="input" required value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
      <div className="field"><label>Email</label><input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
      <div className="field"><label>City</label><input className="input" value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
      <div className="field"><label>Pincode</label><input className="input" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} /></div>
      <div className="field full"><label>Address</label><input className="input" value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
      <div className="field"><label>Emergency contact</label><input className="input" value={form.emergencyName} onChange={(e) => set('emergencyName', e.target.value)} /></div>
      <div className="field"><label>Emergency phone</label><input className="input" value={form.emergencyPhone} onChange={(e) => set('emergencyPhone', e.target.value)} /></div>

      <div className="form-section-title">Clinical & records</div>
      <div className="field"><label>Known allergies</label><input className="input" placeholder="e.g. Penicillin, Sulfa" value={form.allergies} onChange={(e) => set('allergies', e.target.value)} /></div>
      <div className="field"><label>Chronic conditions</label><input className="input" placeholder="Comma separated" value={form.chronicConditions} onChange={(e) => set('chronicConditions', e.target.value)} /></div>
      <div className="field"><label>Current medications</label><input className="input" value={form.currentMeds} onChange={(e) => set('currentMeds', e.target.value)} /></div>
      <div className="field"><label>ABHA / Govt ID</label><input className="input" value={form.abhaId} onChange={(e) => set('abhaId', e.target.value)} /></div>
      <div className="field"><label>Occupation</label><input className="input" value={form.occupation} onChange={(e) => set('occupation', e.target.value)} /></div>
      <div className="field"><label>Marital status</label><select className="input" value={form.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)}>{MARITAL.map((m) => <option key={m} value={m}>{m || '—'}</option>)}</select></div>
    </>
  );
}

function VaccinationTab({ patient, onSave }) {
  const [records, setRecords] = useState(patient.vaccinationRecords || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await ipApi.patchPatient(patient.patientId, { vaccinationRecords: records });
      onSave?.();
    } finally { setBusy(false); }
  };
  return (
    <>
      <VaccinationChart records={records} onChange={setRecords} patientAge={patient.age} />
      <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 12 }} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save vaccination chart'}</button>
    </>
  );
}

function PatientRecordsTab({ patientId }) {
  const { data, loading, reload } = useLiveData(() => ipApi.getPatientDocuments(patientId), [patientId]);
  const docs = data?.documents || [];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200000) { setErr('Max 200 KB per file for now.'); return; }
    setBusy(true); setErr('');
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = String(reader.result).split(',')[1] || '';
        await ipApi.uploadPatientDocument(patientId, {
          fileName: file.name,
          mimeType: file.type,
          category: 'Medical Record',
          contentBase64: base64,
        });
        await reload();
        setBusy(false);
      };
      reader.readAsDataURL(file);
    } catch (ex) { setErr(ex.message); setBusy(false); }
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
          {busy ? 'Uploading…' : '+ Upload record'}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={onUpload} disabled={busy} />
        </label>
        <p className="hint" style={{ marginTop: 6 }}>Reports, scans, and old prescriptions.</p>
      </div>
      {err && <div className="error mb">{err}</div>}
      {loading && <p className="hint">Loading…</p>}
      {!loading && docs.length === 0 && <p className="hint">No uploaded records yet.</p>}
      {docs.map((d) => (
        <div key={d.documentId} className="rcp-visit">
          <div className="rcp-visit-top">
            <span className="rcp-visit-date">{d.fileName}</span>
            <span className="rcp-visit-doc">{d.category} · {d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString('en-IN') : ''}</span>
          </div>
          {d.note && <div className="rcp-visit-line">{d.note}</div>}
        </div>
      ))}
    </>
  );
}

function PatientDrawer({ patient, initialMode = 'view', doctors, defaultFee, onClose, onSaved, onSendToOp, onDeletePatient, canDelete, showVaccination, onSentToQueue }) {
  const [mode, setMode] = useState(initialMode);
  const [tab, setTab] = useState('profile');
  const [editForm, setEditForm] = useState(toForm(patient));
  const [opForm, setOpForm] = useState({ doctor: doctors[0]?.name || '', fee: defaultFee, reason: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const { data: visitData, loading: visitsLoading } = useLiveData(
    () => ipApi.getEmrRecords(patient.patientId, 20), [patient.patientId],
  );
  const { data: admData, loading: admLoading } = useLiveData(
    () => ipApi.getAdmissions(80), [patient.patientId],
  );
  const visits = [...(visitData?.records || visitData?.emr || visitData?.items || [])]
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const ipVisits = (admData?.admissions || []).filter((a) => a.patientId === patient.patientId)
    .sort((a, b) => new Date(b.admittedAt || 0) - new Date(a.admittedAt || 0));

  useEffect(() => { setEditForm(toForm(patient)); setMode(initialMode); setTab('profile'); setMsg(''); setErr(''); }, [patient, initialMode]);

  const set = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const opSet = (k, v) => setOpForm((f) => ({ ...f, [k]: v }));

  const saveEdit = async () => {
    setBusy(true); setErr('');
    try {
      await ipApi.patchPatient(patient.patientId, serialize(editForm));
      setMode('view'); setMsg('Patient details updated.');
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const sendOp = async () => {
    setBusy(true); setErr('');
    try {
      await onSendToOp(patient, opForm);
      setMsg(`Sent to ${opForm.doctor}'s OP queue — collect fee at Q Page.`);
      setMode('view');
      onSentToQueue?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const fullName = `${patient.title ? `${patient.title}. ` : ''}${patient.name}`;
  const ageSex = `${patient.age || '—'}${patient.gender ? ` / ${patient.gender}` : ''}`;

  return (
    <div className="rcp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="rcp-drawer" role="dialog" aria-modal="true">
        <div className="rcp-drawer-head">
          <div className="rcp-drawer-top">
            <div className="rcp-id">
              <div className="rcp-avatar">{initials(patient.name)}</div>
              <div>
                <div className="rcp-name">{fullName}</div>
                <div className="rcp-sub">{patient.patientId} · {ageSex}{patient.bloodGroup ? ` · ${patient.bloodGroup}` : ''}{patient.phone ? ` · ${patient.phone}` : ''}</div>
              </div>
            </div>
            <button type="button" className="rcp-close" onClick={onClose} aria-label="Close">×</button>
          </div>
          {msg && <div className="hint" style={{ marginTop: 10, color: 'var(--color-primary-dark)' }}>{msg}</div>}
          {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
          {mode === 'view' && (
            <div className="rcp-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={() => { setMode('op'); setOpForm({ doctor: doctors[0]?.name || '', fee: defaultFee, reason: '' }); }}>Send to OP</button>
              <Link to={buildIpdAdmitLink({ patientName: patient.name, patientId: patient.patientId, phone: patient.phone })} className="btn btn-sm btn-secondary">Admit to IPD</Link>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setMode('edit')}>Edit details</button>
              <Link to={`/hms/emr?patient=${encodeURIComponent(patient.name)}&patientId=${encodeURIComponent(patient.patientId)}`} className="btn btn-sm btn-secondary">Open EMR</Link>
              {canDelete && onDeletePatient && (
                <button type="button" className="btn btn-sm btn-secondary" style={{ color: '#dc2626' }} onClick={async () => {
                  if (!window.confirm(`Delete patient ${patient.name} (${patient.patientId})? This cannot be undone.`)) return;
                  setBusy(true);
                  try {
                    await onDeletePatient(patient);
                    onClose();
                  } catch (e) { setErr(e.message); } finally { setBusy(false); }
                }}>Delete patient</button>
              )}
            </div>
          )}
        </div>

        {mode === 'view' && (
          <>
            <div className="rcp-tabs">
              <button type="button" className={`rcp-tab ${tab === 'profile' ? 'active' : ''}`} onClick={() => setTab('profile')}>Profile</button>
              <button type="button" className={`rcp-tab ${tab === 'visits' ? 'active' : ''}`} onClick={() => setTab('visits')}>Visit history{visits.length ? ` (${visits.length})` : ''}</button>
              <button type="button" className={`rcp-tab ${tab === 'records' ? 'active' : ''}`} onClick={() => setTab('records')}>Medical records</button>
              {showVaccination && <button type="button" className={`rcp-tab ${tab === 'vaccines' ? 'active' : ''}`} onClick={() => setTab('vaccines')}>Vaccination</button>}
            </div>
            <div className="rcp-body">
              {tab === 'profile' && (
                <>
                  {patient.allergies && (
                    <div className="rcp-alert"><span>⚠️</span><span><b>Allergies:</b> {patient.allergies}</span></div>
                  )}
                  <dl className="rcp-kv">
                    <dt>UHID</dt><dd>{patient.patientId}</dd>
                    <dt>Name</dt><dd>{fullName}</dd>
                    <dt>Age / Gender</dt><dd>{ageSex}</dd>
                    {patient.dob && (<><dt>Date of birth</dt><dd>{new Date(patient.dob).toLocaleDateString('en-IN')}</dd></>)}
                    {patient.bloodGroup && (<><dt>Blood group</dt><dd>{patient.bloodGroup}</dd></>)}
                    <dt>Phone</dt><dd>{patient.phone || '—'}</dd>
                    {patient.email && (<><dt>Email</dt><dd>{patient.email}</dd></>)}
                    {(patient.address || patient.city || patient.pincode) && (
                      <><dt>Address</dt><dd>{[patient.address, patient.city, patient.pincode].filter(Boolean).join(', ')}</dd></>
                    )}
                    {(patient.emergencyName || patient.emergencyPhone) && (
                      <><dt>Emergency</dt><dd>{[patient.emergencyName, patient.emergencyPhone].filter(Boolean).join(' · ')}</dd></>
                    )}
                    {patient.occupation && (<><dt>Occupation</dt><dd>{patient.occupation}</dd></>)}
                    {patient.maritalStatus && (<><dt>Marital status</dt><dd>{patient.maritalStatus}</dd></>)}
                    {patient.abhaId && (<><dt>ABHA / Govt ID</dt><dd>{patient.abhaId}</dd></>)}
                    {Array.isArray(patient.chronicConditions) && patient.chronicConditions.length > 0 && (
                      <><dt>Chronic conditions</dt><dd>{patient.chronicConditions.join(', ')}</dd></>
                    )}
                    {patient.currentMeds && (<><dt>Current meds</dt><dd>{patient.currentMeds}</dd></>)}
                    <dt>Registered</dt><dd>{patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('en-IN') : '—'}</dd>
                  </dl>
                </>
              )}
              {tab === 'records' && <PatientRecordsTab patientId={patient.patientId} />}
              {tab === 'vaccines' && showVaccination && <VaccinationTab patient={patient} onSave={onSaved} />}
              {tab === 'visits' && (
                <>
                  {visitsLoading && admLoading && <div className="hint">Loading visit history…</div>}
                  {!visitsLoading && !admLoading && visits.length === 0 && ipVisits.length === 0 && (
                    <div className="hint">No OP or IP visits recorded for this patient yet.</div>
                  )}
                  {ipVisits.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#0d6e6a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Inpatient (IPD)</div>
                      {ipVisits.map((a) => (
                        <div className="rcp-visit" key={a.admissionId} style={{ borderLeft: '3px solid #0d6e6a' }}>
                          <div className="rcp-visit-top">
                            <span className="rcp-visit-date">{a.admittedAt ? new Date(a.admittedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span>
                            <span className="rcp-visit-doc">{a.doctor || '—'} · {a.status === 'discharged' ? 'Discharged' : 'Admitted'}</span>
                          </div>
                          <div className="rcp-visit-line"><b>IP ID:</b> {a.admissionId} · <b>Bed:</b> {a.bedLabel || a.bedId || '—'}</div>
                          {a.diagnosis && <div className="rcp-visit-line"><b>Diagnosis:</b> {a.diagnosis}</div>}
                          {a.reason && <div className="rcp-visit-line"><b>Reason:</b> {a.reason}</div>}
                          <Link to={`/ipd/patient/${a.admissionId}`} className="btn btn-sm btn-secondary" style={{ marginTop: 8 }}>Open IP chart</Link>
                        </div>
                      ))}
                    </>
                  )}
                  {visits.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em', margin: ipVisits.length ? '16px 0 8px' : '0 0 8px' }}>Outpatient (OPD)</div>
                      {visits.map((v) => (
                        <div className="rcp-visit" key={v.recordId || v.createdAt} style={{ borderLeft: '3px solid #2563eb' }}>
                          <div className="rcp-visit-top">
                            <span className="rcp-visit-date">{v.createdAt ? new Date(v.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span>
                            <span className="rcp-visit-doc">{v.doctorName || v.doctor || ''} · {v.visitType || 'OPD'}</span>
                          </div>
                          {v.complaint && <div className="rcp-visit-line"><b>Complaint:</b> {v.complaint}</div>}
                          {v.diagnosis && <div className="rcp-visit-line"><b>Diagnosis:</b> {v.diagnosis}</div>}
                          {v.prescription && <div className="rcp-visit-line"><b>Rx:</b> {v.prescription.split('\n').slice(0, 3).join('; ')}</div>}
                          {v.advice && <div className="rcp-visit-line"><b>Advice:</b> {v.advice}</div>}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {mode === 'edit' && (
          <div className="rcp-body">
            <form className="form-grid" onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
              <PatientFormFields form={editForm} set={set} />
              <div className="form-actions full row-actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setMode('view')}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {mode === 'op' && (
          <div className="rcp-body">
            <h3 style={{ marginTop: 0 }}>Send {patient.name} to OP queue</h3>
            <form className="form-grid" onSubmit={(e) => { e.preventDefault(); sendOp(); }}>
              <div className="field full">
                <label>Doctor</label>
                <select className="input" value={opForm.doctor} onChange={(e) => opSet('doctor', e.target.value)}>
                  {doctors.length ? doctors.map((d) => <option key={d.staffId} value={d.name}>{formatDoctorOption(d)}</option>) : <option value="">No doctors configured</option>}
                </select>
              </div>
              <div className="field"><label>Consultation fee ₹</label><input className="input" type="number" value={opForm.fee} onChange={(e) => opSet('fee', Number(e.target.value))} /></div>
              <div className="field full"><label>Reason / chief complaint</label><input className="input" placeholder="e.g. Fever & cough for 3 days" value={opForm.reason} onChange={(e) => opSet('reason', e.target.value)} /></div>
              <div className="form-actions full row-actions">
                <button type="submit" className="btn btn-primary" disabled={busy || !opForm.doctor}>{busy ? 'Adding…' : 'Add to queue'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setMode('view')}>Cancel</button>
              </div>
            </form>
          </div>
        )}
      </aside>
    </div>
  );
}

export default function ReceptionPatients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const debouncedSetQuery = useDebouncedSearch(setQuery, 300);
  const { data, loading, error, reload } = useLiveData(() => ipApi.getPatients(query, 40), [query]);
  const patients = data?.patients || [];
  const { data: dir } = useHospitalDirectory();
  const { data: emrData } = useLiveData(() => ipApi.getEmrSettings(), []);
  const emrSettings = mergeEmrSettings(emrData?.settings);
  const doctors = useMemo(() => dir?.doctors || [], [dir]);
  const defaultFee = dir?.hospital?.pricing?.opConsultFee || 500;

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [openMode, setOpenMode] = useState('view');
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  useEffect(() => { debouncedSetQuery(search); }, [search, debouncedSetQuery]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError('');
    try {
      await ipApi.createPatient({ ...serialize(form), hospitalId: user.hospitalId });
      setForm(emptyForm);
      setShowForm(false);
      await reload();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendToOp = async (patient, opForm) => {
    await ipApi.addToQueue({
      patientName: patient.name,
      patientId: patient.patientId,
      patientPhone: patient.phone || '',
      age: patient.age || '',
      gender: patient.gender || 'M',
      doctor: opForm.doctor,
      fee: Number(opForm.fee) || defaultFee,
      reason: opForm.reason || '',
      visitDate: new Date().toISOString().slice(0, 10),
      visitType: 'Follow-up',
      hospitalId: user.hospitalId,
      status: Q.BOOKED,
    });
  };

  const [importBusy, setImportBusy] = useState(false);

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').slice(1).filter(Boolean);
      const patients = lines.map((line) => {
        const [name, phone, gender, age] = line.split(',').map((s) => s.trim());
        return { name, phone, gender, age };
      }).filter((p) => p.name);
      const res = await ipApi.importPatients(patients);
      alert(`Imported ${res.imported} patients.`);
      await reload();
    } catch (err) {
      alert(err.message);
    } finally {
      setImportBusy(false);
    }
  };

  const canMerge = canMergePatients(user?.role);

  const columns = [
    { key: 'patientId', label: 'UHID', render: (r) => <button type="button" className="linklike" onClick={() => { setOpenMode('view'); setSelected(r); }}>{r.patientId}</button> },
    { key: 'name', label: 'Name', render: (r) => (r.title ? `${r.title}. ${r.name}` : r.name) },
    { key: 'age', label: 'Age / Sex', render: (r) => `${r.age || '—'} ${r.gender || ''}` },
    { key: 'phone', label: 'Phone' },
    { key: 'city', label: 'City', render: (r) => r.city || '—' },
    { key: 'createdAt', label: 'Registered', render: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : '—') },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setOpenMode('view'); setSelected(r); }}>View</button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={async () => {
            try {
              const blob = await ipApi.exportPatient(r.patientId);
              const url = URL.createObjectURL(new Blob([JSON.stringify(blob, null, 2)], { type: 'application/json' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `${r.patientId}-export.json`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) { alert(e.message); }
          }}>Export</button>
          {canMerge && (
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setMergeSource(r); setMergeTargetId(''); }}>Merge</button>
          )}
          <button type="button" className="btn btn-sm btn-primary" onClick={() => { setOpenMode('op'); setSelected(r); }}>OP visit</button>
        </div>
      ),
    },
  ];

  return (
    <PageShell
      title="Patients"
      subtitle={`${user?.hospital || 'Hospital'} — UHID register, search & profile`}
      actions={
        (user?.role === 'manager' || user?.role === 'reception') ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            {importBusy ? 'Importing…' : 'Import CSV'}
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={importCsv} disabled={importBusy} />
          </label>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Cancel' : '+ Register patient'}
          </button>
        </div>
        ) : null
      }
    >
      {showForm && (
        <form className="form-grid card mb" onSubmit={handleCreate}>
          <PatientFormFields form={form} set={setField} />
          {saveError && <div className="error full">{saveError}</div>}
          <div className="form-actions full"><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save patient'}</button></div>
        </form>
      )}

      <div className="card mb">
        <input className="input" placeholder="Search name, phone, UHID…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {error && <div className="error mb">{error}</div>}
      <DataTable
        columns={columns}
        rows={patients}
        keyField="patientId"
        loading={loading}
        emptyMessage={search ? 'No patients match your search.' : 'No patients registered yet — click “Register patient” to add the first one.'}
      />

      {selected && (
        <PatientDrawer
          patient={selected}
          initialMode={openMode}
          doctors={doctors}
          defaultFee={defaultFee}
          onClose={() => setSelected(null)}
          onSaved={reload}
          onSendToOp={sendToOp}
          onSentToQueue={() => navigate('/reception/queue')}
          canDelete={user?.role === 'manager' || user?.role === 'reception'}
          onDeletePatient={async (p) => { await ipApi.deletePatient(p.patientId); await reload(); }}
          showVaccination={emrSettings.enableVaccinationChart !== false}
        />
      )}

      {mergeSource && (
        <div className="q-modal-backdrop" onClick={() => setMergeSource(null)}>
          <div className="q-modal card" onClick={(e) => e.stopPropagation()}>
            <h4>Merge patients</h4>
            <p className="hint">Keep <strong>{mergeSource.name}</strong> ({mergeSource.patientId}) and merge duplicate into it.</p>
            <div className="field full">
              <label>Duplicate patient UHID to merge</label>
              <select className="input" value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)}>
                <option value="">— Select —</option>
                {patients.filter((p) => p.patientId !== mergeSource.patientId).map((p) => (
                  <option key={p.patientId} value={p.patientId}>{p.name} · {p.patientId}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setMergeSource(null)}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!mergeTargetId} onClick={async () => {
                try {
                  await ipApi.mergePatients(mergeSource.patientId, mergeTargetId);
                  setMergeSource(null);
                  await reload();
                  alert('Patients merged. EMR records moved to kept UHID.');
                } catch (e) { alert(e.message); }
              }}>Merge</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
